import { Autocomplete, TextField, ListItem, Typography, Tooltip } from '@mui/material';
import { useState, useEffect, SyntheticEvent } from 'react';
import './CreatablePaginateAutocomplete.css';
import useDebounce from '../../shared/hooks/useDebounce';
import useRuleValidation from '../../shared/hooks/useRuleValidation';
import PaginationParams from '../../models/PaginationParams';
import { CreatablePaginateAutocompleteOption, CreatablePaginateAutocompleteOptionClass, CreatablePaginateAutocompleteProps } from './CreatablePaginateAutocompleteProps'
import { t } from 'i18next';
import CircularProgress from '@mui/material/CircularProgress';

const CreatablePaginateAutocomplete = ({
  options,
  value,
  placeholder,
  label,
  disableCondition,
  noOptionsText,
  addOptionText = t('addOptionText'),
  alertMessage,
  conditionToShowAlertMessage,
  required,
  setValue,
  onChange,
  fetchFunc,
  maxLength,
  addOption = true,
  uppercase,
  dataTestId
}: CreatablePaginateAutocompleteProps) => {
  const [pattern, setPattern] = useState('');
  const [patternDebounced, isDebouncing] = useDebounce(pattern, 300);
  const [hideOptions, setHideOptions] = useState(false);

  const defaultRule = useRuleValidation(() => !!value);

  const configParams: PaginationParams = {
    pageSize: 20,
    offset: 0
  };

  useEffect(() => {
    if (!value && options.length > 0) {
      setHideOptions(true);
    }
  }, [value]);

  useEffect(() => {
    if (patternDebounced.trim() === '') {
      setHideOptions(true);

    } else {
      setHideOptions(false);
      const params = { ...configParams, pattern: patternDebounced };
      fetchFunc(params);
    }
  }, [patternDebounced]);

  const changePattern = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pattern = event.target.value;
    setPattern(pattern);
  };

  const onChangeAutocomplete = (value?: CreatablePaginateAutocompleteOption | null, reason?: string) => {
    if (reason === 'clear') {
      setHideOptions(true);
      setPattern('');
    }
    if (value !== undefined) {
      onChange?.();
    }
  };


  const lookingMatches = t('lookingMatches');
  return (
    <>
      <Autocomplete
        role={'autocomplete'}
        fullWidth
        size="small"
        autoHighlight
        options={hideOptions ? [] : options}
        noOptionsText={(pattern && isDebouncing) ? lookingMatches : noOptionsText}
        getOptionLabel={(option: CreatablePaginateAutocompleteOption) => {
          if (typeof option === 'string') {
            return option;
          }
          if (option.inputValue) {
            return option.inputValue;
          }
          return option.name;
        }}
        value={value}
        disabled={disableCondition}
        onChange={(_event: SyntheticEvent, newValue: CreatablePaginateAutocompleteOptionClass | null, reason) => {
          if (typeof newValue === 'string') {
            setValue(new CreatablePaginateAutocompleteOptionClass(undefined, undefined, newValue, undefined));
          } else if (newValue?.id === '') {
            newValue.name = newValue.inputValue.toUpperCase();
            newValue.inputValue = newValue.inputValue.toUpperCase()
            newValue.key = newValue.key.toUpperCase()
            setValue(newValue);
          } else {
            if (newValue) {
              newValue.removeName = true;
            }
            setValue(newValue);
          }
          onChangeAutocomplete(newValue, reason);
        }}
        filterOptions={(options, params) => {
          const filtered = options;
          let { inputValue } = params
          inputValue = inputValue.toUpperCase()
          const isExisting = options.some((option) => inputValue === option.name);
          if (inputValue !== '' && !isExisting && !isDebouncing && addOption) {
            filtered.push(new CreatablePaginateAutocompleteOptionClass(undefined, inputValue, `${addOptionText} "${inputValue}"`, inputValue, undefined));
          }
          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        renderOption={(props, option: CreatablePaginateAutocompleteOption) => <ListItem sx={{ paddingLeft: '5px' }} {...props} key={option?.id}>
          {option.name}
        </ListItem>}
        renderInput={(params) => (
          <div style={{ position: 'relative' }}>
            <Tooltip title={label}>
              <TextField
                data-testid={dataTestId}
                {...params}
                required={required}
                className='REQXXI-CreatablePaginateAutocomplete-TextField'
                fullWidth
                value={pattern}
                label={label}
                placeholder={placeholder}
                onChange={changePattern}
                onBlur={() => setPattern('')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {defaultRule.adornment}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
                inputProps={{
                  ...params.inputProps,
                  maxLength,
                  style: {
                    ...(uppercase ? { textTransform: 'uppercase' } : {})
                  }
                }}
              />
            </Tooltip>
            {(pattern && isDebouncing) && (
              <div style={{ position: 'absolute', top: '58%', right: '70px', transform: 'translateY(-50%)' }}>
                <CircularProgress color="primary" size={20} />
              </div>
            )}
          </div>
        )}
      />
      {(alertMessage && conditionToShowAlertMessage) &&
        <Typography variant="subtitle2" className="REQXXI-CreatablePaginateAutocomplete-AlertMessage"> {alertMessage} </Typography>
      }
    </>
  );
};

export default CreatablePaginateAutocomplete;
